import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Info, Check,
  Upload,
  Camera,
  FileText,
  DollarSign,
  Calculator,
  Shield,
  Zap,
  Clock,
  TrendingUp,
  User,
  MapPin,
  Phone,
  Mail,
  Building,
  CreditCard,
  PieChart,
  Target,
  HelpCircle,
  Lightbulb,
  Star,
  Award,
  Lock,
  Eye,
  EyeOff,
  Sun,
  Moon,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Smartphone,
  Car,
  Home,
  GraduationCap,
  Heart,
  Briefcase,
  Calendar
} from 'lucide-react';

const LoanApplication = ({ theme, isDarkMode })  => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    loanType: '',
    amount: '',
    purpose: '',
    income: '',
    employment: '',
    creditScore: '',
    personalInfo: {},
    documents: {},
    preferences: {}
  });
  const [eligibilityScore, setEligibilityScore] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const loanTypes = [
    { 
      id: 'personal', 
      title: 'Personal Loan', 
      icon: User, 
      rate: '5.99%', 
      maxAmount: '$50,000',
      term: '2-7 years',
      color: 'from-blue-500 to-cyan-500',
      description: 'Flexible financing for any personal need'
    },
    { 
      id: 'auto', 
      title: 'Auto Loan', 
      icon: Car, 
      rate: '3.49%', 
      maxAmount: '$100,000',
      term: '3-8 years',
      color: 'from-green-500 to-emerald-500',
      description: 'Competitive rates for new and used vehicles'
    },
    { 
      id: 'home', 
      title: 'Home Loan', 
      icon: Home, 
      rate: '3.25%', 
      maxAmount: '$750,000',
      term: '15-30 years',
      color: 'from-purple-500 to-pink-500',
      description: 'Make your homeownership dreams reality'
    },
    { 
      id: 'business', 
      title: 'Business Loan', 
      icon: Briefcase, 
      rate: '4.75%', 
      maxAmount: '$500,000',
      term: '1-10 years',
      color: 'from-orange-500 to-red-500',
      description: 'Fuel your business growth and expansion'
    },
    { 
      id: 'education', 
      title: 'Education Loan', 
      icon: GraduationCap, 
      rate: '2.99%', 
      maxAmount: '$200,000',
      term: '5-25 years',
      color: 'from-indigo-500 to-blue-500',
      description: 'Invest in your future with education financing'
    }
  ];

  const steps = [
    { id: 0, title: 'Loan Type', icon: Target, description: 'Choose your loan' },
    { id: 1, title: 'Loan Details', icon: Calculator, description: 'Amount & purpose' },
    { id: 2, title: 'Personal Info', icon: User, description: 'Your information' },
    { id: 3, title: 'Financial Info', icon: DollarSign, description: 'Income & employment' },
    { id: 4, title: 'Documents', icon: FileText, description: 'Required documents' },
    { id: 5, title: 'Review', icon: CheckCircle2, description: 'Final review' }
  ];

  const aiSuggestions = [
    { text: "Based on your income, you're pre-qualified for up to $35,000", type: "success" },
    { text: "Consider a shorter term to save $2,400 in interest", type: "info" },
    { text: "Your credit score qualifies you for our premium rates", type: "success" },
    { text: "Adding a co-signer could improve your rate by 1.2%", type: "info" }
  ];

  const documents = [
    { id: 'id', title: 'Government ID', required: true, uploaded: false, icon: CreditCard },
    { id: 'income', title: 'Proof of Income', required: true, uploaded: false, icon: FileText },
    { id: 'bank', title: 'Bank Statements', required: true, uploaded: false, icon: Building },
    { id: 'employment', title: 'Employment Letter', required: false, uploaded: false, icon: Briefcase }
  ];

  useEffect(() => {
    // Simulate eligibility calculation
    const calculateEligibility = () => {
      setIsCalculating(true);
      setTimeout(() => {
        const score = Math.floor(Math.random() * 30) + 70; // 70-100
        setEligibilityScore(score);
        setIsCalculating(false);
        
        // Generate recommendations based on score
        if (score >= 90) {
          setRecommendations(['Premium rates available', 'Fast approval likely', 'Consider higher amount']);
        } else if (score >= 80) {
          setRecommendations(['Good approval odds', 'Standard rates apply', 'Consider co-signer']);
        } else {
          setRecommendations(['Review application', 'Improve credit score', 'Consider secured loan']);
        }
      }, 2000);
    };

    if (currentStep >= 1 && formData.amount && formData.income) {
      calculateEligibility();
    }
  }, [currentStep, formData.amount, formData.income]);

  const EligibilityMeter = ({ score, isLoading }) => {
    const getColor = (score) => {
      if (score >= 90) return 'text-emerald-500';
      if (score >= 80) return 'text-blue-500';
      if (score >= 70) return 'text-amber-500';
      return 'text-red-500';
    };

    const getLabel = (score) => {
      if (score >= 90) return 'Excellent';
      if (score >= 80) return 'Good';
      if (score >= 70) return 'Fair';
      return 'Needs Improvement';
    };

    return (
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
        <div className="text-center">
          <h3 className={`text-sm font-semibold ${theme.text} mb-3`}>Eligibility Score</h3>
          {isLoading ? (
            <div className="animate-spin w-12 h-12 mx-auto mb-3">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="mb-3">
              <div className={`text-4xl font-bold ${getColor(score)}`}>{score}</div>
              <div className={`text-xs ${getColor(score)} font-medium`}>{getLabel(score)}</div>
            </div>
          )}
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ${
                score >= 90 ? 'bg-emerald-500' :
                score >= 80 ? 'bg-blue-500' :
                score >= 70 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${score}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const LoanCalculator = () => {
    const [amount, setAmount] = useState(formData.amount || '');
    const [term, setTerm] = useState('5');
    const [rate] = useState('5.99');

    const calculateMonthly = () => {
      if (!amount) return 0;
      const principal = parseFloat(amount);
      const monthlyRate = parseFloat(rate) / 100 / 12;
      const numPayments = parseInt(term) * 12;
      
      const monthly = (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                     (Math.pow(1 + monthlyRate, numPayments) - 1);
      
      return monthly.toFixed(2);
    };

    return (
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
        <h3 className={`text-sm font-semibold ${theme.text} mb-4`}>Loan Calculator</h3>
        
        <div className="space-y-4">
          <div>
            <label className={`text-xs ${theme.textMuted} block mb-2`}>Loan Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Enter amount"
            />
          </div>
          
          <div>
            <label className={`text-xs ${theme.textMuted} block mb-2`}>Term (Years)</label>
            <select
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
            >
              <option value="3">3 years</option>
              <option value="5">5 years</option>
              <option value="7">7 years</option>
              <option value="10">10 years</option>
            </select>
          </div>
          
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-4 text-white text-center">
            <div className="text-xs opacity-80 mb-1">Monthly Payment</div>
            <div className="text-2xl font-bold">${calculateMonthly()}</div>
            <div className="text-xs opacity-80">at {rate}% APR</div>
          </div>
        </div>
      </div>
    );
  };

  const AIAssistant = () => (
    <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
          <Lightbulb className="w-4 h-4 text-white" />
        </div>
        <h3 className={`text-sm font-semibold ${theme.text}`}>AI Assistant</h3>
      </div>
      
      <div className="space-y-3">
        {aiSuggestions.map((suggestion, index) => (
          <div key={index} className={`p-3 rounded-xl ${
            suggestion.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20' :
            'bg-blue-500/10 border border-blue-500/20'
          }`}>
            <div className="flex items-start space-x-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                suggestion.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'
              }`}>
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
              <p className={`text-xs ${theme.text}`}>{suggestion.text}</p>
            </div>
          </div>
        ))}
      </div>
      
      <button className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-xl text-sm font-medium">
        Get Personalized Advice
      </button>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Loan Type Selection
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Choose Your Loan Type</h2>
              <p className={`${theme.textMuted}`}>Select the loan that best fits your needs</p>
            </div>
            
            <div className="grid gap-4">
              {loanTypes.map((loan) => (
                <button
                  key={loan.id}
                  onClick={() => setFormData({...formData, loanType: loan.id})}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    formData.loanType === loan.id 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : `border-gray-700 ${theme.cardBg}`
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${loan.color} rounded-xl flex items-center justify-center`}>
                      <loan.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className={`font-semibold ${theme.text}`}>{loan.title}</h3>
                      <p className={`text-sm ${theme.textMuted}`}>{loan.description}</p>
                      <div className="flex space-x-4 mt-2">
                        <span className={`text-xs ${theme.success} font-medium`}>From {loan.rate}</span>
                        <span className={`text-xs ${theme.textMuted}`}>Up to {loan.maxAmount}</span>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${formData.loanType === loan.id ? 'text-blue-500' : theme.textMuted}`} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 1: // Loan Details
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Loan Details</h2>
              <p className={`${theme.textMuted}`}>Tell us about your financing needs</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className={`text-sm font-medium ${theme.text} block mb-2`}>Loan Amount</label>
                  <div className="relative">
                    <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="Enter amount"
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    {['$5K', '$15K', '$25K', '$50K'].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setFormData({...formData, amount: amount.replace('$', '').replace('K', '000')})}
                        className={`px-3 py-1 text-xs rounded-full border ${theme.textMuted} hover:border-blue-500 hover:text-blue-500 transition-colors`}
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={`text-sm font-medium ${theme.text} block mb-2`}>Loan Purpose</label>
                  <select
                    value={formData.purpose}
                    onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                    className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select purpose</option>
                    <option value="debt-consolidation">Debt Consolidation</option>
                    <option value="home-improvement">Home Improvement</option>
                    <option value="medical">Medical Expenses</option>
                    <option value="education">Education</option>
                    <option value="vacation">Vacation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <LoanCalculator />
            </div>

            {eligibilityScore > 0 && (
              <EligibilityMeter score={eligibilityScore} isLoading={isCalculating} />
            )}
          </div>
        );

      case 2: // Personal Information
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Personal Information</h2>
              <p className={`${theme.textMuted}`}>Help us get to know you better</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${theme.text} block mb-2`}>First Name</label>
                <input
                  type="text"
                  className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className={`text-sm font-medium ${theme.text} block mb-2`}>Last Name</label>
                <input
                  type="text"
                  className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <label className={`text-sm font-medium ${theme.text} block mb-2`}>Email</label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                  <input
                    type="email"
                    className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter email"
                  />
                </div>
              </div>
              <div>
                <label className={`text-sm font-medium ${theme.text} block mb-2`}>Phone</label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                  <input
                    type="tel"
                    className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter phone"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className={`text-sm font-medium ${theme.text} block mb-2`}>Address</label>
                <div className="relative">
                  <MapPin className={`absolute left-3 top-3 w-5 h-5 ${theme.textMuted}`} />
                  <textarea
                    className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500 resize-none`}
                    rows="3"
                    placeholder="Enter full address"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Financial Information
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Financial Information</h2>
              <p className={`${theme.textMuted}`}>Help us assess your loan eligibility</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className={`text-sm font-medium ${theme.text} block mb-2`}>Annual Income</label>
                  <div className="relative">
                    <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                    <input
                      type="number"
                      value={formData.income}
                      onChange={(e) => setFormData({...formData, income: e.target.value})}
                      className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter annual income"
                    />
                  </div>
                </div>

                <div>
                  <label className={`text-sm font-medium ${theme.text} block mb-2`}>Employment Status</label>
                  <select
                    value={formData.employment}
                    onChange={(e) => setFormData({...formData, employment: e.target.value})}
                    className={`w-full p-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select status</option>
                    <option value="employed">Full-time Employee</option>
                    <option value="self-employed">Self-employed</option>
                    <option value="contract">Contract Worker</option>
                    <option value="retired">Retired</option>
                    <option value="student">Student</option>
                  </select>
                </div>

                <div>
                  <label className={`text-sm font-medium ${theme.text} block mb-2`}>Company Name</label>
                  <div className="relative">
                    <Building className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                    <input
                      type="text"
                      className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border rounded-xl ${theme.text} focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter company name"
                    />
                  </div>
                </div>
              </div>

              <AIAssistant />
            </div>
          </div>
        );

      case 4: // Documents
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Required Documents</h2>
              <p className={`${theme.textMuted}`}>Upload your documents for quick processing</p>
            </div>

            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        doc.uploaded ? 'bg-emerald-500/20' : 'bg-gray-500/20'
                      }`}>
                        <doc.icon className={`w-5 h-5 ${doc.uploaded ? 'text-emerald-500' : theme.textMuted}`} />
                      </div>
                      <div>
                        <h3 className={`font-medium ${theme.text}`}>{doc.title}</h3>
                        <p className={`text-xs ${theme.textMuted}`}>
                          {doc.required ? 'Required' : 'Optional'} • {doc.uploaded ? 'Uploaded' : 'Not uploaded'}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className={`p-2 ${theme.cardBg} border rounded-xl hover:border-blue-500 transition-colors`}>
                        <Camera className={`w-4 h-4 ${theme.textMuted}`} />
                      </button>
                      <button className={`p-2 ${theme.cardBg} border rounded-xl hover:border-blue-500 transition-colors`}>
                        <Upload className={`w-4 h-4 ${theme.textMuted}`} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border border-blue-500/50 bg-blue-500/5`}>
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className={`text-sm font-medium ${theme.text} mb-1`}>Document Security</h4>
                  <p className={`text-xs ${theme.textMuted}`}>
                    All documents are encrypted with bank-level security. We never store sensitive information permanently.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 5: // Review
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Review Application</h2>
              <p className={`${theme.textMuted}`}>Please review your information before submitting</p>
            </div>

            <div className="space-y-4">
              <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
                <h3 className={`font-semibold ${theme.text} mb-3`}>Loan Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Loan Type</span>
                    <span className={theme.text}>Personal Loan</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Amount</span>
                    <span className={theme.text}>${formData.amount || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Purpose</span>
                    <span className={theme.text}>{formData.purpose || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Annual Income</span>
                    <span className={theme.text}>${formData.income || '0'}</span>
                  </div>
                </div>
              </div>

              <EligibilityMeter score={eligibilityScore} isLoading={false} />

              <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl p-4 border`}>
                <h3 className={`font-semibold ${theme.text} mb-3`}>Next Steps</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className={`text-sm ${theme.text}`}>Instant pre-approval decision</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className={`text-sm ${theme.text}`}>Full approval within 24-48 hours</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Zap className="w-5 h-5 text-purple-500" />
                    <span className={`text-sm ${theme.text}`}>Funds available in 1-3 business days</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Ready to Submit?</h3>
                    <p className="text-sm opacity-90 mb-4">Your application looks great! Submit now for instant pre-approval.</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4" />
                        <span>Secure</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Zap className="w-4 h-4" />
                        <span>Instant</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Award className="w-4 h-4" />
                        <span>Best Rates</span>
                      </div>
                    </div>
                  </div>
                  <Star className="w-16 h-16 opacity-20" />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Invalid step</div>;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg} transition-all duration-300`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 p-2 pt-12">
        <div className="flex items-center space-x-4">
          <button className={`w-10 h-10 ${theme.cardBg} border rounded-xl flex items-center justify-center hover:scale-105 transition-all`}>
            <ArrowLeft className={`w-5 h-5 ${theme.text}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${theme.text}`}>Apply for Loan</h1>
            <p className={`text-sm ${theme.textMuted}`}>Step {currentStep + 1} of {steps.length}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          
          
          <button 
            onClick={() => setShowAIAssistant(!showAIAssistant)}
            className={`w-10 h-10 ${theme.cardBg} border rounded-xl flex items-center justify-center hover:scale-105 transition-all ${showAIAssistant ? 'bg-purple-500 border-purple-500' : ''}`}
          >
            <Lightbulb className={`w-5 h-5 ${showAIAssistant ? 'text-white' : 'text-purple-500'}`} />
          </button>
          
          <button className={`w-10 h-10 ${theme.cardBg} border rounded-xl flex items-center justify-center hover:scale-105 transition-all`}>
            <HelpCircle className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
        <div className="px-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center space-y-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                index <= currentStep 
                  ? 'bg-blue-500 border-blue-500 text-white' 
                  : `border-gray-600 ${theme.textMuted}`
              }`}>
                {index < currentStep ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <step.icon className="w-5 h-5" />
                )}
              </div>
              
            </div>
          ))}
        </div>
        
        <div className="w-full bg-gray-700 rounded-full h-1">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-1 rounded-full transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>


      {/* Main Content */}
      <div className="px-4 pb-32">
        {renderStepContent()}
        
         {/* Bottom Navigation */}
     
        <div className="px-4 mt-4">
            <div className={`${theme.cardBg} backdrop-blur-xl rounded-2xl border ${theme.border} p-3 relative overflow-hidden`}>
                {/* Subtle background gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-50"></div>
                
                <div className="flex items-center justify-between relative z-10">
                
                {/* Back Button with Enhanced Feedback */}
                <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className={`relative p-2.5 rounded-xl transition-all group ${
                    currentStep === 0 
                        ? 'opacity-40 cursor-not-allowed' 
                        : 'hover:bg-gray-500/10 active:scale-95'
                    }`}
                    aria-label="Previous step"
                >
                    <ArrowLeft className={`w-4 h-4 ${theme.text} transition-transform group-hover:-translate-x-0.5`} />
                    
                    {/* Ripple effect */}
                    <span className="absolute inset-0 bg-white/10 rounded-xl scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300"></span>
                </button>

                {/* Interactive Progress Visualization */}
                <div className="flex-1 mx-3">
                    <div className="flex items-center justify-center space-x-1 mb-2">
                    {steps.map((_, index) => (
                        <button
                        key={index}
                        onClick={() => setCurrentStep(index)}
                        className="relative group"
                        aria-label={`Go to step ${index + 1}`}
                        >
                        {/* Progress Bar Segment */}
                        <div className="w-3 h-1.5 rounded-full bg-gray-200 mx-0.5 overflow-hidden">
                            <div 
                            className={`h-full transition-all duration-500 ${
                                index <= currentStep 
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500' 
                                : ''
                            }`}
                            style={{ width: index < currentStep ? '100%' : index === currentStep ? '50%' : '0%' }}
                            ></div>
                        </div>
                        
                        {/* Step Tooltip */}
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className={`text-xs font-medium px-2 py-1 rounded-lg ${
                            isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                            } whitespace-nowrap`}>
                            {steps[index].title}
                            </div>
                        </div>
                        </button>
                    ))}
                    </div>
                    
                    {/* Current Step Indicator */}
                    <div className="text-center">
                    <div className={`text-xs font-medium ${
                        currentStep === steps.length - 1 ? 'text-emerald-500' : 'text-blue-500'
                    }`}>
                        {currentStep + 1}/{steps.length}
                    </div>
                    </div>
                </div>

                {/* Advanced Action Button */}
                <button
                    onClick={() => {
                    if (currentStep === steps.length - 1) {
                        return;
                    } else {
                        setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
                    }
                    }}
                    className={`relative p-2.5 rounded-xl font-medium transition-all group overflow-hidden ${
                    currentStep === steps.length - 1
                        ? 'bg-emerald-500 hover:bg-emerald-600 shadow-lg'
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-md'
                    } text-white active:scale-95`}
                    aria-label={currentStep === steps.length - 1 ? 'Submit application' : 'Next step'}
                >
                    {/* Button Content */}
                    {currentStep === steps.length - 1 ? (
                    <Check className="w-4 h-4" />
                    ) : (
                    <ArrowRight className="w-4 h-4" />
                    )}
                    
                    {/* Shine Effect */}
                    <span className="absolute inset-0 bg-white/10 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                    
                    {/* Pulse Animation for Final Step */}
                    {currentStep === steps.length - 1 && (
                    <span className="absolute inset-0 bg-white/20 rounded-xl animate-ping opacity-0 group-hover:opacity-100"></span>
                    )}
                </button>

                </div>
            </div>
        </div>
      </div>

      {/* AI Assistant Overlay */}
      {showAIAssistant && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center p-4">
          <div className={`w-full max-w-md ${theme.cardBg} rounded-3xl border p-6 transform transition-all duration-300`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-white" />
                </div>
                <h3 className={`font-semibold ${theme.text}`}>AI Loan Assistant</h3>
              </div>
              <button 
                onClick={() => setShowAIAssistant(false)}
                className={`w-8 h-8 ${theme.cardBg} border rounded-xl flex items-center justify-center`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-4 border border-purple-500/20">
                <h4 className={`font-medium ${theme.text} mb-2`}>Smart Recommendations</h4>
                <ul className="space-y-2">
                  {recommendations.map((rec, index) => (
                    <li key={index} className={`text-sm ${theme.textSecondary} flex items-center space-x-2`}>
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-3">
                <button className="w-full p-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium">
                  Optimize My Application
                </button>
                <button className="w-full p-3 border border-purple-500/50 text-purple-500 rounded-xl font-medium">
                  Get Rate Comparison
                </button>
                <button className="w-full p-3 border border-emerald-500/50 text-emerald-500 rounded-xl font-medium">
                  Check Credit Impact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}      

    </div>
  );
};

export default LoanApplication;
